import React from "react";
import { ArrowUpRight } from "lucide-react";
import { PWA } from "@/constants/testIds";
import { GALLERY } from "./data";
import Reveal from "./Reveal";

const Gallery = () => {
  const go = (id) => document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });
  const featured = GALLERY.find((g) => g.featured) || GALLERY[0];
  const rest = GALLERY.filter((g) => g !== featured);

  return (
    <section
      id="gallery"
      data-testid={PWA.gallerySection}
      className="relative bg-[#FAF9F6] py-24 md:py-32"
    >
      <div className="w-full max-w-7xl mx-auto px-6 md:px-10 lg:px-14">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="text-[11px] tracking-[0.4em] uppercase text-[#5C4033] font-semibold mb-5">
                — The Work
              </p>
            </Reveal>
            <Reveal delay={120}>
              <h2 className="font-serif-display text-[#1A1A1A] text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                Handmade in the shop, <br />
                <span className="italic text-[#5C4033]">shown here as-is.</span>
              </h2>
            </Reveal>
          </div>
          <Reveal delay={200} className="lg:col-span-5">
            <p className="text-[#1A1A1A]/75 text-base md:text-lg leading-relaxed font-light">
              A living gallery of finished pieces — from signature end-grain boards and
              keepsake boxes to bespoke display bases and specialty gifts.
            </p>
          </Reveal>
        </div>

        {/* Featured hero item */}
        <Reveal>
          <a
            href="#quote"
            onClick={(e) => {
              e.preventDefault();
              go("#quote");
            }}
            data-testid={PWA.galleryItem(featured.key)}
            className="gallery-card block bg-[#1A1A1A] mb-6 md:mb-8 relative"
          >
            <img
              src={featured.image}
              alt={featured.title}
              className="w-full h-[420px] md:h-[620px] object-cover"
            />
            <div className="gallery-overlay">
              <p className="text-[10px] tracking-[0.32em] uppercase text-[#C19A6B] font-semibold">
                Featured Piece
              </p>
              <h3 className="font-serif-display text-[#FAF9F6] text-3xl md:text-5xl mt-2 leading-tight max-w-2xl">
                {featured.title}
              </h3>
              <div className="mt-3 flex items-center justify-between">
                <p className="text-[#FAF9F6]/80 text-sm tracking-wide">{featured.caption}</p>
                <span className="inline-flex items-center gap-2 text-[#FAF9F6] text-[11px] tracking-[0.28em] uppercase">
                  Inquire <ArrowUpRight className="w-3.5 h-3.5" />
                </span>
              </div>
            </div>
          </a>
        </Reveal>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {rest.map((g, i) => (
            <Reveal key={g.key} delay={i * 60}>
              <a
                href="#quote"
                onClick={(e) => {
                  e.preventDefault();
                  go("#quote");
                }}
                data-testid={PWA.galleryItem(g.key)}
                className="gallery-card block bg-[#1A1A1A] cursor-pointer h-[360px] md:h-[440px]"
              >
                <img src={g.image} alt={g.title} className="w-full h-full object-cover" />
                <div className="gallery-overlay">
                  <p className="text-[10px] tracking-[0.32em] uppercase text-[#C19A6B] font-semibold">
                    {g.caption}
                  </p>
                  <h3 className="font-serif-display text-[#FAF9F6] text-2xl md:text-3xl mt-2 leading-tight">
                    {g.title}
                  </h3>
                  <div className="mt-3 inline-flex items-center gap-2 text-[#FAF9F6] text-[11px] tracking-[0.28em] uppercase">
                    Inquire <ArrowUpRight className="w-3.5 h-3.5" />
                  </div>
                </div>
              </a>
            </Reveal>
          ))}
        </div>

        <Reveal delay={120}>
          <div className="mt-14 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 border-t border-[#1A1A1A]/10 pt-10">
            <p className="font-serif-display text-2xl md:text-3xl text-[#1A1A1A] max-w-xl">
              Have a piece in mind? Let's design it together.
            </p>
            <button
              onClick={() => go("#quote")}
              className="btn-walnut"
              data-testid="pwa-gallery-cta-quote"
            >
              Request a Quote
              <ArrowUpRight className="w-4 h-4" />
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Gallery;
